import { RECOVER_LOCAL_AUDIO_DEBOUNCE_MS } from './constants';
import type { JanusCallRefs } from './sessionRefs';
import { logJanus } from './utils';
import { listAudioInputDeviceIds, listAudioOutputDeviceIds } from './webDevices';

const SPEAKER_PERMISSION_NAMES = ['speaker', 'audio-playback'] as const;

export type PermissionSyncState = 'granted' | 'denied' | 'unknown';

export async function queryOptionalPermission(name: string): Promise<PermissionStatus | null> {
  try {
    return await navigator.permissions.query({ name: name as PermissionName });
  } catch {
    return null;
  }
}

export async function getMicrophonePermissionState(): Promise<PermissionSyncState> {
  const status = await queryOptionalPermission('microphone');
  if (!status) return 'unknown';
  if (status.state === 'denied') return 'denied';
  if (status.state === 'granted') return 'granted';
  return 'unknown';
}

export async function getSiteSoundPermissionState(): Promise<PermissionSyncState> {
  let hasUnknown = false;
  for (const name of SPEAKER_PERMISSION_NAMES) {
    const status = await queryOptionalPermission(name);
    if (!status) continue;
    if (status.state === 'denied') return 'denied';
    if (status.state === 'granted') return 'granted';
    hasUnknown = true;
  }
  return hasUnknown ? 'unknown' : 'unknown';
}

export function clearMicrophoneBlocked(r: JanusCallRefs): void {
  r.micDisconnectHandledRef.current = false;
  r.isMutedRef.current = false;
  r.localStreamRef.current?.getAudioTracks().forEach((track) => {
    track.enabled = true;
  });
  logJanus('MIC', 'microphone available — clear mic UI');
  r.onLocalAudioRecoveredRef.current?.();
}

export function markMicrophoneBlocked(r: JanusCallRefs): void {
  if (r.micDisconnectHandledRef.current) return;
  r.micDisconnectHandledRef.current = true;
  logJanus('MIC', 'microphone blocked — UI warn only');
  r.onAudioHardwareLostRef.current?.({ target: 'mic', reason: 'not_found' });
}

export function markSpeakerUnplugged(r: JanusCallRefs): void {
  r.speakerPlaybackWarnedRef.current = true;
  logJanus('AUDIO', 'audio output removed — UI warn only');
  r.onAudioHardwareLostRef.current?.({ target: 'speaker', reason: 'unplugged' });
}

export function markSpeakerBlocked(r: JanusCallRefs): void {
  if (r.speakerPlaybackWarnedRef.current) return;
  r.speakerPlaybackWarnedRef.current = true;
  logJanus('AUDIO', 'site sound blocked — UI warn only');
  r.onAudioHardwareLostRef.current?.({ target: 'speaker', reason: 'playback' });
}

export function clearSpeakerBlocked(r: JanusCallRefs): void {
  r.speakerPlaybackWarnedRef.current = false;
  logJanus('AUDIO', 'site sound available — clear speaker UI');
  r.onRemotePlaybackRecoveredRef.current?.();
  void r.recoverRemotePlaybackRef.current();
}

export async function handleMicrophonePermissionGranted(r: JanusCallRefs): Promise<void> {
  const track = r.localStreamRef.current?.getAudioTracks()[0];
  if (track?.readyState === 'live') {
    clearMicrophoneBlocked(r);
    return;
  }

  logJanus('MIC', 'permission granted — schedule recoverLocalAudio');
  if (!r.micDisconnectHandledRef.current) {
    r.micDisconnectHandledRef.current = true;
  }
  scheduleRecoverLocalAudio(r);
}

export async function syncMicrophonePermissionUi(
  r: JanusCallRefs,
  micPermissionStatus: PermissionStatus | null,
  isActiveCallSession: () => boolean
): Promise<void> {
  if (!isActiveCallSession() || !micPermissionStatus) return;

  if (micPermissionStatus.state === 'denied') {
    markMicrophoneBlocked(r);
    return;
  }

  if (micPermissionStatus.state === 'granted') {
    await handleMicrophonePermissionGranted(r);
  }
}

export async function syncSpeakerPermissionUi(
  r: JanusCallRefs,
  isActiveCallSession: () => boolean
): Promise<void> {
  if (!isActiveCallSession()) return;

  const sound = await getSiteSoundPermissionState();
  if (sound === 'denied') {
    markSpeakerBlocked(r);
    return;
  }

  if (sound === 'granted') {
    clearSpeakerBlocked(r);
  }
}

export async function tryMarkSpeakerBlockedFromPlaybackProbe(
  r: JanusCallRefs,
  isActiveCallSession: () => boolean
): Promise<void> {
  if (!isActiveCallSession()) return;
  if (r.speakerPlaybackWarnedRef.current) return;

  const sound = await getSiteSoundPermissionState();
  if (sound !== 'unknown') return;

  const mic = await getMicrophonePermissionState();
  if (mic === 'denied') return;

  const ok = await r.recoverRemotePlaybackRef.current();
  if (!ok) {
    markSpeakerBlocked(r);
  }
}

export async function attachSpeakerPermissionListener(
  onChange: (state: PermissionState) => void
): Promise<(() => void) | null> {
  const statuses: PermissionStatus[] = [];

  for (const name of SPEAKER_PERMISSION_NAMES) {
    const status = await queryOptionalPermission(name);
    if (!status) continue;
    const handler = () => onChange(status.state);
    status.onchange = handler;
    statuses.push(status);
  }

  if (!statuses.length) return null;

  return () => {
    statuses.forEach((s) => {
      s.onchange = null;
    });
  };
}

export async function resyncCallAudioToBrowserDefaults(
  r: JanusCallRefs,
  isActiveCallSession: () => boolean
): Promise<void> {
  if (!isActiveCallSession()) return;

  logJanus('AUDIO', 'resync mic + speaker to browser default devices');

  const micPerm = await getMicrophonePermissionState();
  if (micPerm !== 'denied') {
    scheduleRecoverLocalAudio(r);
  }

  const playOk = await r.recoverRemotePlaybackRef.current();
  if (playOk) {
    clearSpeakerBlocked(r);
    return;
  }

  const outputs = await listAudioOutputDeviceIds();
  if (outputs.length === 0) {
    markSpeakerUnplugged(r);
  } else {
    markSpeakerBlocked(r);
  }
}

/** @deprecated use resyncCallAudioToBrowserDefaults */
export const tryRecoverAudioAfterDevicePlugIn = resyncCallAudioToBrowserDefaults;

export function scheduleRecoverLocalAudio(r: JanusCallRefs): void {
  if (r.recoverLocalAudioRunningRef.current) return;

  const now = Date.now();
  if (now - r.lastRecoverLocalAudioAtRef.current < RECOVER_LOCAL_AUDIO_DEBOUNCE_MS) {
    return;
  }

  r.recoverLocalAudioRunningRef.current = true;
  void (async () => {
    try {
      const ok = await r.recoverLocalAudioRef.current();
      r.lastRecoverLocalAudioAtRef.current = Date.now();
      if (!ok) {
        r.micDisconnectHandledRef.current = true;
        logJanus('MIC', 'recoverLocalAudio failed — keep mic UI warn');
        const inputs = await listAudioInputDeviceIds();
        r.onAudioHardwareLostRef.current?.({
          target: 'mic',
          reason: inputs.length === 0 ? 'not_found' : 'unplugged',
        });
      }
    } finally {
      r.recoverLocalAudioRunningRef.current = false;
    }
  })();
}
